import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  getPublicCenterSettlementPayment,
  submitPublicCenterSettlementPayment,
  initiatePublicCenterSettlementCardPayment,
  downloadPublicCenterSettlementInvoicePdf
} from '../../../services/crmApi';
import { uploadFilePublic } from '../../../services/fileApi';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';

const formatMoney = (currency, amount) => {
  const value = Number(amount) || 0;
  const formatted = value.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'LKR' || !currency ? `Rs ${formatted}` : `${currency} ${formatted}`;
};

const PublicCenterSettlementPay = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState(null);
  const [done, setDone] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [form, setForm] = useState({
    amount: '',
    paymentMethod: 'Bank Transfer',
    paymentDate: new Date().toISOString().slice(0, 10),
    reference: '',
    notes: ''
  });

  useEffect(() => {
    const paycorp = String(searchParams.get('paycorp') || '').toLowerCase();
    if (paycorp === 'success') {
      toast.success('Card payment completed successfully');
      setDone(true);
    } else if (paycorp === 'failed' || paycorp === 'error') {
      toast.error('Card payment failed');
    } else if (paycorp === 'cancelled') {
      toast.error('Card payment was cancelled');
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getPublicCenterSettlementPayment(token);
        if (cancelled) return;
        const data = res.data?.data || res.data;
        setMeta(data);
        setForm((prev) => ({
          ...prev,
          amount: data?.amountDue != null ? String(data.amountDue) : prev.amount
        }));
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load settlement payment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      if (form.paymentMethod === 'Card') {
        if (meta?.cardPaymentDisabled) {
          toast.error('Card payment is temporarily disabled');
          return;
        }
        const res = await initiatePublicCenterSettlementCardPayment(token, {
          amount: Number(form.amount)
        });
        const url = res.data?.data?.paymentPageUrl;
        if (!url) {
          toast.error('Unable to start card payment');
          return;
        }
        window.location.href = url;
        return;
      }

      let receiptFileId = null;
      if (receiptFile) {
        const uploadRes = await uploadFilePublic(receiptFile, {
          category: 'Payment Receipt',
          entityType: 'CenterSettlementInvoice',
          title: receiptFile.name,
          description: `Settlement payment for ${meta?.invoiceNumber || token}`
        });
        const f = uploadRes?.data?.data || uploadRes?.data;
        receiptFileId = f?._id || f?.fileId || null;
      }

      await submitPublicCenterSettlementPayment(token, {
        ...form,
        amount: Number(form.amount),
        receiptFileId
      });
      setDone(true);
      toast.success('Payment submitted for SLIM verification');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-sm text-gray-600">
        Loading settlement payment…
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-sm text-red-600">
        Payment link unavailable.
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Payment submitted</h1>
          <p className="mt-2 text-sm text-gray-600">
            Your payment for invoice {meta.invoiceNumber} was received
            {String(searchParams.get('paycorp') || '').toLowerCase() === 'success'
              ? ' and processed via card.'
              : ' and is pending SLIM approval.'}
          </p>
        </div>
      </div>
    );
  }

  const methods = (meta.allowedMethods || ['Bank Transfer', 'Cheque', 'Mobile Payment', 'Other', 'Card']).map((m) => ({
    value: m,
    label: m === 'Card'
      ? (meta.cardPaymentEnabled ? 'Card (online gateway)' : 'Card (temporarily disabled)')
      : m,
    disabled: m === 'Card' && meta.cardPaymentDisabled
  }));

  const isCard = form.paymentMethod === 'Card';
  const needsUpload = !isCard;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-white px-4 py-10">
      <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">Center → SLIM</p>
            <h1 className="text-xl font-semibold text-gray-900 mt-1">Settlement payment</h1>
            <p className="text-sm text-gray-600 mt-1">
              {meta.centerName || 'Franchise center'} · Invoice {meta.invoiceNumber}
            </p>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const res = await downloadPublicCenterSettlementInvoicePdf(token);
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `${meta.invoiceNumber || 'settlement-invoice'}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to download invoice PDF');
              }
            }}
            className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            Invoice PDF
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-indigo-50 border border-indigo-100 p-3">
          <div>
            <p className="text-[10px] uppercase text-indigo-500 font-semibold">Amount due</p>
            <p className="text-lg font-semibold text-indigo-900">
              {formatMoney(meta.currency, meta.amountDue)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-indigo-500 font-semibold">Invoice total</p>
            <p className="text-lg font-semibold text-indigo-900">
              {formatMoney(meta.currency, meta.netAmount)}
            </p>
            {Number(meta.amountApproved) > 0 ? (
              <p className="text-[11px] text-emerald-700 mt-0.5">
                Approved paid {formatMoney(meta.currency, meta.amountApproved)}
              </p>
            ) : null}
          </div>
        </div>

        {meta.breakdown && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="rounded border border-gray-100 p-2">
              <p className="text-gray-400 uppercase font-semibold">Gross to SLIM</p>
              <p className="font-medium text-gray-800">{formatMoney(meta.currency, meta.breakdown.grossCenterOwesSlim)}</p>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <p className="text-gray-400 uppercase font-semibold">Volume discount</p>
              <p className="font-medium text-gray-800">{formatMoney(meta.currency, meta.breakdown.volumeDiscountAmount)}</p>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <p className="text-gray-400 uppercase font-semibold">Center keeps</p>
              <p className="font-medium text-gray-800">{formatMoney(meta.currency, meta.breakdown.amountCenterKeeps)}</p>
            </div>
            <div className="rounded border border-gray-100 p-2">
              <p className="text-gray-400 uppercase font-semibold">SLIM share</p>
              <p className="font-medium text-gray-800">{formatMoney(meta.currency, meta.breakdown.amountSlimShare)}</p>
            </div>
          </div>
        )}

        {(meta.lines || []).length > 0 && (
          <div className="mt-4 overflow-x-auto border border-gray-100 rounded-lg">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">Application</th>
                  <th className="px-2 py-1.5 text-left">Payable</th>
                  <th className="px-2 py-1.5 text-left">Center keep</th>
                  <th className="px-2 py-1.5 text-left">SLIM share</th>
                  <th className="px-2 py-1.5 text-left">Owed</th>
                </tr>
              </thead>
              <tbody>
                {meta.lines.map((line) => {
                  const components = Array.isArray(line.financialBreakdown?.components)
                    ? line.financialBreakdown.components.filter((c) => Number(c?.amount) > 0)
                    : [];
                  return (
                    <tr key={line.applicationNumber} className="border-t border-gray-50 align-top">
                      <td className="px-2 py-1.5">
                        <div className="font-medium text-gray-800">{line.applicationNumber}</div>
                        <div className="text-gray-500">{[line.studentName, line.courseName].filter(Boolean).join(' · ')}</div>
                        {components.length > 0 && (
                          <div className="mt-1 space-y-0.5 text-[10px] text-gray-500">
                            {components.map((c) => (
                              <div key={`${c.code || c.label}-${c.amount}`} className="flex justify-between gap-2">
                                <span>{c.label || c.code}</span>
                                <span>{formatMoney(meta.currency, c.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{formatMoney(meta.currency, line.payableAmount ?? line.paidAmount)}</div>
                        {line.courseFee != null && Number(line.courseFee) !== Number(line.payableAmount) ? (
                          <div className="text-gray-400">Course {formatMoney(meta.currency, line.courseFee)}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5">{formatMoney(meta.currency, line.centerKeep)}</td>
                      <td className="px-2 py-1.5">{formatMoney(meta.currency, line.slimShare)}</td>
                      <td className="px-2 py-1.5 font-medium">{formatMoney(meta.currency, line.amountOwed)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              required
              value={form.amount}
              onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment method *</label>
            <select
              required
              value={form.paymentMethod}
              onChange={(e) => setForm((p) => ({ ...p, paymentMethod: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {methods.map((m) => (
                <option key={m.value} value={m.value} disabled={m.disabled}>{m.label}</option>
              ))}
            </select>
          </div>
          {!isCard && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment date *</label>
                <input
                  type="date"
                  required
                  value={form.paymentDate}
                  onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={form.reference}
                  onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Bank slip / transfer reference"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
          {needsUpload && (
            <ProofAttachmentDropzone
              file={receiptFile}
              onChange={setReceiptFile}
              hint={form.paymentMethod === 'Bank Transfer' ? '(recommended)' : '(optional)'}
            />
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting
              ? (isCard ? 'Redirecting…' : 'Submitting…')
              : (isCard ? 'Pay with card' : 'Submit payment')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicCenterSettlementPay;
